import React from 'react';
import { Link } from 'react-router-dom';
import './styles/landingPage.css';

const productScreenshots = [
  {
    src: '/landing/01_fleet_dashboard_desktop.svg',
    alt: 'ManageLift Fleet Dashboard showing fleet maintenance and health statuses for Northstar Distribution Center',
    title: 'Fleet Dashboard',
    description: 'See fleet health and maintenance priorities at a glance.',
    featured: true,
  },
  {
    src: '/landing/03_operator_fl101_desktop.svg',
    alt: 'ManageLift operator workflow showing forklift FL-101 available to start a checklist',
    title: 'Operator Workflow',
    description: 'Give operators a clear path into pre-shift inspections.',
  },
  {
    src: '/landing/02_technician_dashboard_desktop.svg',
    alt: 'ManageLift Technician Dashboard showing fleet maintenance and health priorities',
    title: 'Technician Dashboard',
    description: 'Surface the lifts that need maintenance attention first.',
  },
  {
    src: '/landing/04_fl101_service_history_desktop.svg',
    alt: 'ManageLift service history for FL-101 with connected operator checklist and maintenance records',
    title: 'Service History',
    description: 'Keep inspections and technician service records connected to the unit.',
  },
];

function LandingActions({ className = '' }) {
  return (
    <div className={`landing-actions ${className}`.trim()}>
      <Link to="/signup" className="landing-action-link landing-action-primary">Start 90-Day Trial</Link>
      <a href="#product-workflow" className="landing-action-link landing-action-secondary">See How It Works</a>
    </div>
  );
}

export default function LandingPage() {
  const featuredScreenshot = productScreenshots.find(({ featured }) => featured);

  return (
    <div className="landing-page">
      <header className="landing-header">
        <div className="landing-container landing-header-inner">
          <Link to="/" className="landing-brand" aria-label="ManageLift home">
            <img src="/Logo.png" alt="ManageLift" />
          </Link>
          <div className="landing-actions landing-header-actions">
            <Link to="/login" className="landing-action-link landing-action-secondary">Sign In</Link>
          </div>
        </div>
      </header>

      <main>
        <section className="landing-hero">
          <div className="landing-container landing-hero-grid">
            <div className="landing-hero-copy">
              <p className="landing-eyebrow">Forklift fleet management software</p>
              <h1>Keep forklift maintenance, repairs, inspections, and service history in one place.</h1>
              <p className="landing-lead">
                ManageLift gives warehouse, maintenance, and operations teams a shared view of each lift instead of
                leaving records scattered across paper, spreadsheets, and service notes.
              </p>
              <LandingActions />
            </div>
            {featuredScreenshot && (
              <figure className="landing-hero-proof">
                <img src={featuredScreenshot.src} alt={featuredScreenshot.alt} />
                <figcaption>
                  <strong>{featuredScreenshot.title}</strong>
                  <span>{featuredScreenshot.description}</span>
                </figcaption>
              </figure>
            )}
          </div>
        </section>

        <section className="landing-section landing-flexibility">
          <div className="landing-container landing-narrow">
            <p className="landing-eyebrow">Work at your pace</p>
            <h2>Keep the work orders, inspection forms, and maintenance forms you already use.</h2>
            <p>
              ManageLift adds shared forklift visibility and service history without requiring you to replace the forms
              and processes your team already relies on.
            </p>
          </div>
        </section>

        <section className="landing-section landing-value-section">
          <div className="landing-container landing-narrow">
            <p className="landing-eyebrow">Know what needs attention</p>
            <h2>Bring scattered forklift records into one shared view.</h2>
            <p>
              Paper forms, spreadsheets, and disconnected service notes make it hard to know what was inspected,
              what is down, and what needs maintenance next. ManageLift puts inspection visibility, maintenance
              cycles, repairs, downtime, and unit records around the forklift in one place.
            </p>
          </div>
        </section>

        <section className="landing-section">
          <div className="landing-container">
            <div className="landing-section-heading">
              <p className="landing-eyebrow">One workflow across the operation</p>
              <h2>Give every role the context they need.</h2>
            </div>
            <div className="landing-card-grid landing-role-grid">
              <article className="landing-card"><h3>Operators</h3><p>Complete forklift inspections and report safety or equipment issues.</p></article>
              <article className="landing-card"><h3>Technicians</h3><p>See which lifts need attention and document maintenance and repairs.</p></article>
              <article className="landing-card"><h3>Fleet Managers / Account Owners</h3><p>See fleet health, maintenance status, service history, and records associated with each unit.</p></article>
            </div>
          </div>
        </section>

        {productScreenshots.length > 0 && (
          <section id="product-workflow" className="landing-section landing-proof-section">
            <div className="landing-container">
              <div className="landing-section-heading">
                <p className="landing-eyebrow">ManageLift in action</p>
                <h2>See the shared workflow from every perspective.</h2>
              </div>
              <div className="landing-proof-grid">
                {productScreenshots.filter(({ featured }) => !featured).map(({ src, alt, title, description }) => (
                  <figure className="landing-product-shot" key={src}>
                    <img src={src} alt={alt} />
                    <figcaption><strong>{title}</strong><span>{description}</span></figcaption>
                  </figure>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="landing-section">
          <div className="landing-container">
            <div className="landing-section-heading"><p className="landing-eyebrow">Straightforward pricing</p><h2>Price your active fleet.</h2><p className="landing-trial">Start with a 90-day trial.</p></div>
            <div className="landing-card-grid landing-pricing-grid">
              <article className="landing-card landing-price-card"><h3>Monthly</h3><p className="landing-price">$5.99</p><p>per active forklift / month</p></article>
              <article className="landing-card landing-price-card"><h3>Annual</h3><p className="landing-price">$59.99</p><p>per forklift / year</p></article>
            </div>
            <p className="landing-billing-note">Retired forklifts are not part of the active billable fleet.</p>
          </div>
        </section>

        <section className="landing-final-cta">
          <div className="landing-container landing-narrow"><h2>Bring your forklift workflow into focus.</h2><p>Start your 90-day trial or see how ManageLift supports your team.</p><LandingActions /></div>
        </section>
      </main>

      <footer className="landing-footer">
        <div className="landing-container landing-footer-inner">
          <span>© ManageLift</span>
          <nav aria-label="Legal and support">
            <Link to="/terms">Terms</Link><Link to="/privacy">Privacy</Link><Link to="/subprocessors">Subprocessors</Link><a href="mailto:support@managelift.com">support@managelift.com</a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
